import { BubbleMenu, BubbleMenuProps, Editor, isTextSelection } from '@tiptap/react'
import cn from 'classnames'
import React, { FC, useCallback, MouseEvent, useState, useRef, useMemo } from 'react'

import ColorPicker from './ColorPicker'
import MarkButton from './MarkButton'
import NodeSelector from './NodeSelector'
import styles from './Toolbar.module.scss'
import { useMarks } from './utils'

import Popup from '~common/Popup'
import ImageIcon from '~icons/editor/image.svg'
import LinkIcon from '~icons/editor/link.svg'
import TableIcon from '~icons/editor/table.svg'
import WandIcon from '~icons/wand.svg'
import { PortalRoots, popupSettings } from '~lib/constants'
import { getSelectedText } from '~lib/editor/helpers/selection'
import useGetElementById from '~lib/hooks/useGetElementById'
import { convertFileListToArray } from '~lib/utils/files'
import AiAssistMenu from '~modules/aiAssist/containers/AiAssistMenu/AiAssistMenu'

interface ToolbarProps {
  opened: boolean
  editor: Editor
  aiAssistEnabled?: boolean
  mediaEnabled?: boolean
  tableEnabled?: boolean
  className?: string
  onCreateLink: () => void
  toggleLoading: (value: boolean) => void
}

function isTextSelected(editor: Editor | BubbleMenuProps['editor'] /* FIXME */) {
  const { doc, selection } = editor.state
  const { empty, from, to } = selection

  // Sometime check for `empty` is not enough.
  // Doubleclick an empty paragraph returns a node size of 2.
  // So we check also for an empty text size.
  const isEmptyTextBlock = isTextSelection(selection) && doc.textBetween(from, to).length === 0

  return empty || isEmptyTextBlock || from === to
}

function shouldShowToolbar({ editor }: Parameters<BubbleMenuProps['shouldShow']>[0]) {
  if (!editor.isEditable || editor.isActive('image')) return false

  return editor.view.hasFocus() && !isTextSelected(editor)
}

const Toolbar: FC<React.PropsWithChildren<ToolbarProps>> = ({
  toggleLoading,
  opened,
  editor,
  className,
  mediaEnabled = false,
  tableEnabled = false,
  aiAssistEnabled,
  onCreateLink,
}) => {
  const modalRoot = useGetElementById(PortalRoots.MODAL_ROOT)

  const tippyConfig: BubbleMenuProps['tippyOptions'] = useMemo(
    () => ({ placement: 'top', maxWidth: null, appendTo: modalRoot }),
    [modalRoot]
  )

  const { marks, toggleMark, isActive } = useMarks(editor)
  const isTable = editor.isActive('table')

  const handleMarkClick = useCallback(
    ({ currentTarget }: MouseEvent<HTMLButtonElement>) => {
      toggleMark(currentTarget.dataset.value)
    },
    [toggleMark]
  )

  const handleAddImageClick = useCallback(() => {
    const { to } = editor.state.selection
    editor.chain().focus(to).run()
    const input = document.createElement('input')
    input.type = 'file'
    input.style.display = 'none'

    input.addEventListener('change', () => {
      const images = convertFileListToArray(input.files)
      editor.commands.insertImages(images, {
        onSuccess: () => {
          input.remove()
        },
      })
    })

    input.addEventListener('blur', () => {
      input.remove()
    })

    document.body.append(input)
    input.click()
  }, [editor])

  const handleInsertTable = useCallback(() => {
    editor.commands.focus()

    requestAnimationFrame(() => {
      const { from } = editor.state.selection
      editor.commands.insertContent(' ')

      editor
        .chain()
        .focus(from - 1)
        .insertTable({ cols: 3, rows: 3, withHeaderRow: false })
        .run()
    })
  }, [editor])

  function handleCreateLinkClick() {
    editor.commands.focus()
    onCreateLink()
  }

  const [aiAssitsExpanded, setAiAssitsExpanded] = useState(false)

  const handleShow = useCallback(() => {
    setAiAssitsExpanded(true)
  }, [])

  const handleHide = useCallback(() => {
    setAiAssitsExpanded(false)
  }, [])

  const handleGetSelectedText = useCallback(() => {
    return getSelectedText(editor)
  }, [editor])

  const positionRef = useRef<number>()

  const handleGenerationStart = useCallback(() => {
    editor.setEditable(false)
    const { to } = editor.state.selection
    positionRef.current = to
    toggleLoading(true)
  }, [editor, toggleLoading])

  const handleGenerationEnd = useCallback(() => {
    editor.setEditable(true)
    toggleLoading(false)
  }, [editor, toggleLoading])

  const handleInsertContent = useCallback(
    (generatedText: string) => {
      const position = positionRef.current

      editor
        .chain()
        .insertContentAt(position, {
          type: 'paragraph',
          content: [{ type: 'text', text: generatedText }],
        })
        .setTextSelection(position) // reset text selection to prevent open bubble menu
        .focus()
        .run()
      handleGenerationEnd()
    },
    [editor, handleGenerationEnd]
  )

  return (
    <BubbleMenu editor={editor} pluginKey="toolbar" tippyOptions={tippyConfig} shouldShow={shouldShowToolbar}>
      {opened && (
        <div className={cn(styles.root, className)}>
          <NodeSelector editor={editor} />

          {marks.map(({ name, icon }) => (
            <MarkButton key={name} onClick={handleMarkClick} value={name} active={isActive(name)}>
              {icon}
            </MarkButton>
          ))}

          <ColorPicker editor={editor} />

          <MarkButton onClick={handleCreateLinkClick}>
            <LinkIcon />
          </MarkButton>

          {mediaEnabled && (
            <MarkButton onClick={handleAddImageClick}>
              <ImageIcon />
            </MarkButton>
          )}

          {tableEnabled && !isTable && (
            <MarkButton onClick={handleInsertTable}>
              <TableIcon />
            </MarkButton>
          )}
          {aiAssistEnabled && (
            <>
              <Popup
                onShow={handleShow}
                onHide={handleHide}
                content={
                  <AiAssistMenu
                    text={handleGetSelectedText}
                    onStart={handleGenerationStart}
                    onError={handleGenerationEnd}
                    onComplete={handleInsertContent}
                  />
                }
                {...popupSettings}
              >
                <MarkButton active={aiAssitsExpanded}>
                  <WandIcon />
                  AI assist
                </MarkButton>
              </Popup>
            </>
          )}
        </div>
      )}
    </BubbleMenu>
  )
}

export default Toolbar
